import type { JSX } from "react";
import { useCallback, useMemo } from "react";

import isEmpty from "lodash/isEmpty";
import last from "lodash/last";
import type { SectionListProps as BaseSectionListProps } from "react-native";
import { SectionList as BaseSectionList, RefreshControl } from "react-native";

import type {
  ListSeparatorType,
  SectionSeparatorSize,
} from "@/ui/layout/list/ListComponents";
import {
  EmptyListComponent,
  SectionSeparatorComponent,
  SpaceSeparatorComponent,
} from "@/ui/layout/list/ListComponents";
import { WebContentConstraints } from "@/ui/layout/ScreenContainer";
import { LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface SectionListProps<T> extends BaseViewProps {
  contentContainerPaddingBottom?: number;
  emptyListComponent?: () => JSX.Element;
  isDataLoading?: boolean;
  isHorizontal?: boolean;
  isRefreshControlLoading?: boolean;
  keyExtractor?: BaseSectionListProps<T>["keyExtractor"];
  ListFooterComponent?: BaseSectionListProps<T>["ListFooterComponent"];
  ListHeaderComponent?: BaseSectionListProps<T>["ListHeaderComponent"];
  onEndReached?: BaseSectionListProps<T>["onEndReached"];
  onEndReachedThreshold?: BaseSectionListProps<T>["onEndReachedThreshold"];
  onRefresh?: () => void;
  renderItem: BaseSectionListProps<T>["renderItem"];
  renderSectionHeader?: BaseSectionListProps<T>["renderSectionHeader"];
  sections: Section<T>[];
  sectionSeparatorSize?: SectionSeparatorSize;
  separatorType?: ListSeparatorType;
  shouldShowRefreshControl?: boolean;
}

// eslint-disable-next-line no-restricted-syntax -- this component has many props as it is a pass-through to the very complex Sectionlist react-native component
const SectionList = <T,>({
  contentContainerPaddingBottom: paddingBottom = 0,
  emptyListComponent,
  isDataLoading,
  isHorizontal,
  isRefreshControlLoading = false,
  keyExtractor,
  ListFooterComponent,
  ListHeaderComponent,
  onEndReached,
  onEndReachedThreshold,
  onRefresh,
  renderItem,
  renderSectionHeader,
  sections,
  sectionSeparatorSize,
  separatorType = "space",
  shouldShowRefreshControl,
  testID,
}: SectionListProps<T>) => {
  const baseTestID = joinTestIDs(testID, "section-list");
  const refreshControl = useMemo(() => {
    if (shouldShowRefreshControl) {
      return (
        <RefreshControl
          onRefresh={onRefresh}
          refreshing={isRefreshControlLoading}
        />
      );
    }
    return undefined;
  }, [isRefreshControlLoading, onRefresh, shouldShowRefreshControl]);
  const ItemSeparatorComponent = useCallback(
    () => (
      <SpaceSeparatorComponent
        isHorizontal={isHorizontal}
        separatorType={separatorType}
      />
    ),
    [isHorizontal, separatorType],
  );
  const ListEmptyComponent = useCallback(
    () => (
      <EmptyListComponent
        contentComponent={emptyListComponent}
        isEmpty={isEmpty(sections)}
        isLoading={isDataLoading}
        testID={joinTestIDs(baseTestID, "empty-list")}
      />
    ),
    [baseTestID, emptyListComponent, sections, isDataLoading],
  );
  return (
    <BaseSectionList
      // on web, maxWidth+center content; ScrollView stays full-width so scroll works viewport-wide.
      contentContainerStyle={{
        ...(isHorizontal ? {} : WebContentConstraints),
        paddingBottom,
      }}
      horizontal={isHorizontal}
      ItemSeparatorComponent={ItemSeparatorComponent}
      keyExtractor={keyExtractor}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      ListHeaderComponent={ListHeaderComponent}
      onEndReached={onEndReached}
      onEndReachedThreshold={onEndReachedThreshold}
      refreshControl={refreshControl}
      renderItem={renderItem}
      renderSectionFooter={({ section }) => {
        const listSections = sections;
        // eslint-disable-next-line sonarjs/different-types-comparison -- currently reported in SonarQube as unresolved
        const isLastSection = last(listSections) === section;

        if (isLastSection) {
          return null;
        }

        return (
          <SectionSeparatorComponent
            isHorizontal={isHorizontal}
            size={sectionSeparatorSize}
          />
        );
      }}
      renderSectionHeader={renderSectionHeader}
      sections={sections}
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
      stickySectionHeadersEnabled={false}
      style={{
        paddingHorizontal: isHorizontal
          ? 0
          : LayoutToken.ContentPaddingHorizontal,
        width: "100%",
      }}
      testID={baseTestID}
    />
  );
};

export type { SectionListProps };
export default SectionList;
